<div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 space-y-2 border-b border-zinc-200 p-4 dark:border-zinc-800 flex flex-col gap-3">
    <h4 class="text-sm font-semibold text-primary m-0">Brand vouchers</h4>
    <p class="text-xs text-secondary m-0">
      Disable an active voucher to expire it immediately so it can no longer be redeemed. Open a row to see who redeemed it.
    </p>

    @if (loadingState() === 'loaded' && vouchers().length > 0) {
      <label class="grid w-full max-w-md grid-cols-1" for="admin-voucher-search">
        <span class="sr-only">Search vouchers by name</span>
        <input
          id="admin-voucher-search"
          type="search"
          autocomplete="off"
          class="input-icon col-start-1 row-start-1 text-primary"
          placeholder="Search by name"
          [value]="searchQuery()"
          (input)="onSearchInput($event)" />
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-secondary sm:size-4"
          aria-hidden="true">
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      </label>
    }
  </div>

  @if (loadingState() === 'loading') {
    <p class="p-4 text-sm text-secondary m-0">Loading vouchers…</p>
  } @else if (loadingState() === 'error') {
    <p class="p-4 text-sm text-secondary m-0">Failed to load vouchers.</p>
  } @else if (vouchers().length === 0) {
    <p class="p-4 text-sm text-secondary m-0">No vouchers have been issued yet.</p>
  } @else if (filteredVouchers().length === 0) {
    <p class="p-4 text-sm text-secondary m-0">No vouchers match your search.</p>
  } @else {
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-950">
      <div class="min-h-0 flex-1 overflow-auto scrollbar-transparent">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
            <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-zinc-800/80">
              <tr>
                <th
                  scope="col"
                  class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300"
                  [attr.aria-sort]="nameSort() === 'asc' ? 'ascending' : 'descending'">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-indigo-700 dark:text-zinc-300 dark:hover:text-indigo-400"
                    (click)="toggleNameSort()">
                    Name
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                      class="size-3.5" aria-hidden="true"
                      [class.rotate-180]="nameSort() === 'desc'">
                      <path d="m5 12 7-7 7 7" />
                      <path d="M12 19V5" />
                    </svg>
                  </button>
                </th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Amount</th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Status</th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Expires</th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Redemptions</th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Closed</th>
                <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-950">
              @for (voucher of filteredVouchers(); track voucher.id) {
                <tr
                  class="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900/80"
                  tabindex="0"
                  (click)="openVoucher(voucher)"
                  (keydown)="onRowKeydown($event, voucher)">
                  <td class="px-4 py-3 text-sm text-primary">
                    <div class="flex items-center gap-1.5">
                      <p class="m-0 min-w-0 truncate font-medium">{{ voucher.name }}</p>
                      <button
                        type="button"
                        class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                        [attr.aria-label]="'Copy ' + voucher.name"
                        (click)="copyName($event, voucher.name)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                          class="size-4" aria-hidden="true">
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    <p class="m-0">{{ voucher.amount / 100 | currency:voucher.currencyCode.toUpperCase() }}</p>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    <p class="m-0">{{ statusLabel(voucher.status) }}</p>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    <p class="m-0">{{ voucher.expiresAt | date:'mediumDate' }}</p>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    <p class="m-0">{{ voucher.redemptionCount ?? 0 }}</p>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    <p class="m-0">
                      @if (terminalDate(voucher); as closedAt) {
                        Expired · {{ closedAt | date:'mediumDate' }}
                      } @else {
                        —
                      }
                    </p>
                  </td>
                  <td class="px-4 py-3 text-sm text-primary">
                    @if (isActive(voucher)) {
                      <button
                        type="button"
                        class="btn-shout btn-shout-xs btn-shout-alert"
                        [disabled]="disablingId() === voucher.id"
                        (click)="disable($event, voucher)">
                        {{ disablingId() === voucher.id ? 'Disabling…' : 'Disable' }}
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  }
</div>
